import { GlobeIcon, ShuffleIcon } from "lucide-react";

import { usePromptSharePinnedVersionData } from "./usePromptSharePinnedVersionData";

import {
  PinnedVersionSelector,
  ShareSessionCreateViewLayout,
  ShareSessionModeSelector,
  ShareSessionSettingRow,
  type PinnedVersionSelection,
} from "@/components/SharedSessions";

type InteractionMode = "batch" | "chat";

interface CreateViewProps {
  name: string;
  onNameChange: (value: string) => void;
  isPublic: boolean;
  onIsPublicChange: (value: boolean) => void;
  mode: InteractionMode;
  onModeChange: (mode: InteractionMode) => void;
  versionSelection: PinnedVersionSelection;
  onVersionSelectionChange: (value: PinnedVersionSelection) => void;
  remixEnabled: boolean;
  onRemixEnabledChange: (value: boolean) => void;
  hasExistingSessions: boolean;
  isCreateDisabled: boolean;
  isPending: boolean;
  onCreate: () => void;
  onBack: () => void;
  hideModeSelector?: boolean;
}

export const CreateView = ({
  name,
  onNameChange,
  isPublic,
  onIsPublicChange,
  mode,
  onModeChange,
  versionSelection,
  onVersionSelectionChange,
  remixEnabled,
  onRemixEnabledChange,
  hasExistingSessions,
  isCreateDisabled,
  isPending,
  onCreate,
  onBack,
  hideModeSelector = false,
}: CreateViewProps) => {
  const { versions, labels } = usePromptSharePinnedVersionData();

  return (
    <ShareSessionCreateViewLayout
      title="Share this prompt"
      description="Create a link to share this prompt."
      namePlaceholder="Link name, e.g. Customer Demo"
      name={name}
      onNameChange={onNameChange}
      hasExistingSessions={hasExistingSessions}
      isCreateDisabled={isCreateDisabled}
      isPending={isPending}
      onCreate={onCreate}
      onBack={onBack}
      settings={
        <>
          {!hideModeSelector && <ShareSessionModeSelector mode={mode} onChange={onModeChange} />}
          <PinnedVersionSelector
            value={versionSelection}
            onChange={onVersionSelectionChange}
            versions={versions}
            labels={labels}
            selectTriggerClassName="h-7 w-[160px]"
            latestDescription="Always use the latest"
            labelDescription="Pinned to release label"
            versionDescription="Pinned to a specific version"
          />
          <ShareSessionSettingRow
            icon={GlobeIcon}
            label="Public access"
            description={
              isPublic
                ? "Visible to anyone with the link"
                : "Restricted to people in your organization"
            }
            checked={isPublic}
            onChange={onIsPublicChange}
          />
          <ShareSessionSettingRow
            icon={ShuffleIcon}
            label="Remixing"
            description="Visitors can copy this prompt"
            checked={remixEnabled}
            onChange={onRemixEnabledChange}
          />
        </>
      }
    />
  );
};
